'use client';
import { Fragment, useEffect, useState } from 'react';
import { getDataBySlug, getSectionDataByName } from '@/libs/sanity/client';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import GeneralComponent from '@/components/GeneralComponent';
import GeneralFullWidthComponent, {
  isGeneralFullWidthComponent,
} from '@/components/FullWidthGeneralComponent';
import Sidebar, { sidebarItem } from '@/components/Sidebar';
import ModuleOverview from '@/components/ModuleOverview/ModuleOverview';
import { convertToSlug } from '@/utils';
import { useMediaQuery } from 'usehooks-ts';
import { TABLET_SCREEN_SIZE } from '@/utils/constants';
import SkeletonLoader from '@/components/SkeletonLoader';

const Title = (
  <h3 className="text-lg pb-8 max-w-[80%]">
    Explore Simplicity<sup>®</sup> <br />
    <span className="text-primary drop-shadow-md font-medium">
      Business Software
    </span>
  </h3>
);

export default function Home() {
  const isTablet = useMediaQuery(`(max-width: ${TABLET_SCREEN_SIZE}px)`);
  const [data, setData] = useState(null);
  const [simplicitySectorModulesData, setSimplicitySectorModulesData] =
    useState(null);
  const [sidebarItems, setSidebarItems] = useState<sidebarItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function fetchData() {
      try {
        const [pageData, simplicitySectorData] = await Promise.all([
          getDataBySlug('/'),
          getSectionDataByName('Simplicity® Sectors'),
        ]);

        setData(pageData);

        const sectorModulesData = simplicitySectorData?.modules.find(
          (item: { name: string; modules: any[] }) =>
            item.name === 'Simplicity® Sectors'
        );
        setSimplicitySectorModulesData(sectorModulesData);

        // Create sidebarItems
        const newSidebarItems: sidebarItem[] = pageData.modules
          .filter(
            (module: any) => module.name && !isGeneralFullWidthComponent(module)
          )
          .map((module: any, index: number) => ({
            id: index,
            content: module.name,
            href: '#',
            isActive: index === 0,
            type: module._type,
            hash: convertToSlug(module.name),
          }));

        newSidebarItems.push({
          id: newSidebarItems.length,
          content: 'Simplicity® Sectors',
          href: '#',
          isActive: false,
          type: 'simplicity-sectors',
          hash: 'simplicity-sectors',
        });

        setSidebarItems(newSidebarItems);
      } catch (err) {
        setError(err as Error);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, []);

  if (error) {
    return <div>Error: {error.message}</div>;
  }

  if (loading || !data || !simplicitySectorModulesData) {
    return <SkeletonLoader />;
  }

  return (
    <>
      <Header />
      <main className="min-h-screen bg-base-100 px-4 xl:px-8 pt-[72px]">
        {data.modules.map((module: any) => (
          <GeneralFullWidthComponent
            key={module._key}
            {...module}
          ></GeneralFullWidthComponent>
        ))}
        <section
          id="content-wrapper"
          className="max-w-8xl grid grid-cols-4 gap-4 mx-auto"
        >
          {!isTablet && sidebarItems.length > 0 && (
            <div id="sidebar" className="mt-4 col-span-0 md:col-span-1">
              <Sidebar title={Title} sidebarItems={sidebarItems} />
            </div>
          )}
          <div id="content" className="col-span-4 xl:col-span-3">
            {data?.modules?.map((module: any, index: number) => {
              module.anchor = convertToSlug(module?.name);
              return (
                <Fragment key={`${module?._key}-${index}`}>
                  <GeneralComponent
                    key={module?._key}
                    {...module}
                  ></GeneralComponent>
                  {index === 1 && (
                    <hr
                      key={index}
                      className="bg-neutral-100 mt-6 mb-12 h-[1.5px]"
                    />
                  )}
                </Fragment>
              );
            })}
            <hr className="bg-neutral-100 mt-6 mb-12 h-[1.5px]" />
          </div>
        </section>
        <section className="max-w-8xl grid grid-cols-4 gap-4 mx-auto">
          <ModuleOverview
            key="module-overview"
            {...simplicitySectorModulesData}
          />
        </section>
      </main>
      <Footer />
    </>
  );
}
